<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import Placeholder from '@tiptap/extension-placeholder'
import { ESection } from '@/components/base/Email'
import EmailBlockWrapper from './EmailBlockWrapper.vue'
import EmailTiptapToolbarWrapper from '../tiptap/EmailTiptapToolbarWrapper.vue'
import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import { createEmailEditorExtensions } from '../../utils/tiptapExtensions'
import { deriveFooterFontSize } from '../../utils/apiTransformers'
import { emailVariables, getVariableOptions } from '../../constants/variables'
import type { FooterAlignment, FooterBlock, TiptapContent } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  block: FooterBlock
  isSelected: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'update:block', block: FooterBlock): void
  (e: 'update:content', content: TiptapContent): void
}>()

const isInternalUpdate = ref(false)

const variables = computed(() => getVariableOptions(emailVariables))

const { user, merchantName } = useLocalStorage()

const tiptapPlainText = (node: TiptapContent | undefined): string => {
  if (!node) return ''
  if (typeof node.text === 'string') return node.text
  return (node.content ?? []).map(tiptapPlainText).join('\n').trim()
}

const isTiptapLegacyDuplicate = (content: TiptapContent | undefined): boolean => {
  const text = tiptapPlainText(content)
  if (!text) return false
  const defaults = [
    merchantName.value,
    user.value?.merchant?.name,
    'Votre entreprise',
    'Votre adresse',
    'Your company',
    'Your address'
  ]
    .filter(Boolean)
    .map((v) => (v as string).trim())
  return text.split('\n').every((line) => defaults.includes(line.trim()))
}

const emptyTiptap: TiptapContent = { type: 'doc', content: [{ type: 'paragraph' }] }

const merchantNameTiptap = (name: string): TiptapContent => ({
  type: 'doc',
  content: [{ type: 'paragraph', content: [{ type: 'text', text: name }] }]
})

const computeInitialContent = (): TiptapContent => {
  const fallbackName = merchantName.value || user.value?.merchant?.name || ''
  if (isTiptapLegacyDuplicate(props.block.content)) {
    return fallbackName ? merchantNameTiptap(fallbackName) : emptyTiptap
  }
  if (!tiptapPlainText(props.block.content) && fallbackName) {
    return merchantNameTiptap(fallbackName)
  }
  return props.block.content
}

const initialContent: TiptapContent = computeInitialContent()

onMounted(() => {
  if (initialContent !== props.block.content) {
    emit('update:content', initialContent)
  }
})

const setAlign = (align: FooterAlignment) => {
  emit('update:block', { ...props.block, align })
}

const editor = useEditor({
  content: initialContent,
  extensions: [
    ...createEmailEditorExtensions(),
    Placeholder.configure({
      placeholder: t('pages.email.emailTemplateCreatePage.panel.defaultText')
    })
  ],
  editorProps: {
    attributes: {
      class: 'focus:outline-none'
    }
  },
  onUpdate: ({ editor }) => {
    isInternalUpdate.value = true
    emit('update:content', editor.getJSON())
    nextTick(() => {
      isInternalUpdate.value = false
    })
  }
})

watch(
  () => props.block.content,
  (newValue) => {
    if (isInternalUpdate.value) return
    if (editor.value) {
      editor.value.commands.setContent(newValue, { emitUpdate: false })
    }
  },
  { deep: true }
)

const insertVariable = (code: string) => {
  editor.value?.commands.insertVariable(code)
}

const unsubscribeFontSize = computed(() => deriveFooterFontSize(props.block.content) ?? '14px')

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<template>
  <EmailBlockWrapper
    :is-selected="isSelected"
    :label="t('pages.email.emailTemplateCreatePage.blockFooter')"
    cursor="text"
    rounded-edge="bottom"
    :block-background-color="block.blockBackgroundColor"
    @select="emit('select')"
  >
    <ESection
      :style="{
        textAlign: block.align,
        padding: '20px 0'
      }"
    >
      <table :style="{ width: '100%' }">
        <tbody>
          <tr :style="{ width: '100%' }">
            <td :align="block.align">
              <EditorContent
                :editor="editor"
                class="footer-editor"
                :style="{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: `#${block.color}`,
                  marginTop: '12px',
                  marginBottom: '12px'
                }"
              />
            </td>
          </tr>

          <tr :style="{ width: '100%' }">
            <td
              :align="block.align"
              :style="{
                fontSize: unsubscribeFontSize,
                color: `#${block.color}`,
                paddingTop: '24px'
              }"
            >
              {{ t('pages.email.emailTemplateCreatePage.footerUnsubscribeText') }}<br />
              <a href="#" :style="{ color: `#${block.color}`, textDecoration: 'underline' }" @click.prevent>
                {{ t('pages.email.emailTemplateCreatePage.footerUnsubscribeLink') }}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </ESection>
  </EmailBlockWrapper>

  <EmailTiptapToolbarWrapper
    :editor="editor"
    :visible="isSelected"
    :variables="variables"
    :color-presets="colorPresets"
    hide-align
    :block-align="block.align"
    @insert-variable="insertVariable"
    @update:block-align="setAlign"
  />

  <Teleport v-if="isSelected" to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="block.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => emit('update:block', { ...block, blockBackgroundColor: v })"
        />
      </EmailPanelSection>
    </div>
  </Teleport>
</template>

<style scoped>
@reference "@/input.css";

.footer-editor :deep(.ProseMirror) {
  @apply outline-none min-h-5;
}

.footer-editor :deep(p) {
  @apply mb-1 mt-0 mx-0;
}

.footer-editor :deep(p:last-child) {
  @apply mb-0;
}

.footer-editor :deep(h1) {
  font-size: 32px;
  @apply font-bold leading-tight m-0;
}
.footer-editor :deep(h2) {
  font-size: 24px;
  @apply font-semibold leading-snug m-0;
}
.footer-editor :deep(h3) {
  font-size: 20px;
  @apply font-semibold leading-normal m-0;
}
.footer-editor :deep(h4) {
  font-size: 18px;
  @apply font-semibold leading-normal m-0;
}
.footer-editor :deep(h5) {
  font-size: 16px;
  @apply font-semibold leading-normal m-0;
}
.footer-editor :deep(h6) {
  font-size: 14px;
  @apply font-semibold leading-normal m-0;
}

.footer-editor :deep(a) {
  @apply text-blue-600 underline;
}

.footer-editor :deep(p.is-editor-empty:first-child::before) {
  @apply text-muted-foreground italic float-left h-0 pointer-events-none;
  content: attr(data-placeholder);
}

.footer-editor :deep(.variable-node),
.footer-editor :deep(.variable-tag) {
  @apply inline-flex items-center px-2 py-0.5 mx-0.5 bg-muted border border-border rounded text-sm font-medium text-foreground-secondary;
}
</style>
